<script lang="ts">
export default {
  inheritAttrs: false,
};
</script>
<script lang="ts" setup>
import { QSpinner } from "quasar";
import { computed, getCurrentInstance, onBeforeMount, ref, useAttrs, useSlots, watch } from "vue";
import MValidationComponent from "./../MValidationComponent";

import InputHelper from "../../../InputHelper.vue";

import { Key } from "ts-key-enum";
import {
  InputRules,
  useHorizontalLabel,
  useInGroup,
  useInputRules,
  useInputWidth,
  usePlaceholder,
} from "../../../../composables/horizontalLabel";
import { validAttribute } from "../../../../utils/misc";

import { checkLabel, randomID } from "../../../../utils/helpers";
import { ActionModelValue } from "../../MAction";
import MButton from "../../MButton";

// TODO, number masks, negative values, integer formats.
const props = withDefaults(
  defineProps<{
    // just to prevent from overriding the modelValue on <input/>
    action?: ActionModelValue;
    actions?: ActionModelValue;
    actionsLeft?: boolean;
    autocomplete?: string;
    autofocus?: boolean;
    autogrow?: boolean;
    border?: boolean;
    clearable?: boolean;
    counter?: boolean;
    counterText?: string | ((len: number, str: string, max?: number) => string);
    debounce?: number | string;
    decimalPlaces?: number | string;
    disable?: boolean;
    disabled?: boolean;
    for?: string;
    helper?: string;
    hideFocusOutline?: boolean;
    horizontal?: boolean;
    inline?: boolean;
    inputFormat?: string | undefined;
    inputRole?: string;
    instructions?: string;
    invalid?: boolean;
    label?: string;
    labelWidth?: string;
    large?: boolean;
    loading?: boolean;
    mask?: string;
    max?: number | string;
    maxNumberLength?: number;
    maxText?: string;
    maxlength?: number | string;
    maxlengthText?: string;
    medium?: boolean;
    min?: number | string;
    minText?: string;
    modelValue?: string | number | null;
    name?: string;
    noValidation?: boolean;
    onClick?: (e: MouseEvent) => void;
    onKeydown?: (e: KeyboardEvent) => void;
    pad?: boolean;
    placeholder?: string;
    requiredText?: string;
    role?: string;
    rules?: InputRules;
    showAllErrors?: boolean;
    showClear?: boolean;
    small?: boolean;
    srInstructions?: string;
    srLabel?: string;
    step?: number | string;
    suffix?: string;
    type?: string;
    uppercase?: boolean;
    value?: any;
  }>(),
  {
    autocomplete: "off",
    maxNumberLength: 2,
    showClear: undefined,
    type: "number",
    border: true,
  },
);

onBeforeMount(() => {
  if (!props.label && !props.srLabel) {
    // TODO, this is temporary as checkLabel does not work anymore
    // but it should prevent/reduce a lot of WCAG errors
    console.warn(`Either label or srLabel must be provided`);
  }
});

const inputValue = ref<string | number>(props.modelValue);

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal !== inputValue.value) {
      inputValue.value = newVal ?? "";
    }
  },
);

type FormatNumberOpts = {
  maxInt: number;
  decimalPlaces?: number;
  pad?: boolean;
};

function formatValue(newValue: string, opts: FormatNumberOpts) {
  const { maxInt, decimalPlaces = 0, pad = false } = opts;

  const raw = String(newValue);

  let s = raw.replace(/[^\d.-]/g, "");
  if (s.startsWith("-")) s = "-" + s.slice(1).replace(/-/g, "");
  else s = s.replace(/-/g, "");

  if (s === "-") {
    inputValue.value = "-";
    emit("update:modelValue", "-");
    emit("change", "-");
    return;
  }

  // --- Collapse extra dots ---
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
  }

  const negative = s.startsWith("-");
  const withoutSign = negative ? s.slice(1) : s;
  const hadDot = withoutSign.includes(".");
  const trailingDot = hadDot && /\.\s*$/.test(withoutSign);
  let [rawInt = "", rawFrac = ""] = withoutSign.split(".", 2);

  // digits only in parts
  let int = rawInt.replace(/\D/g, "");
  let frac = rawFrac.replace(/\D/g, "");

  // ---- KEY FIX: keep the LAST maxInt digits so typing grows on the right unless we are padding ----
  if (int.length > maxInt) int = pad ? int.slice(-maxInt) : int.slice(0, maxInt);

  // ----- Pad: unconditional when pad=true and we have some int -----
  if (pad && int.length > 0) {
    int = int.padStart(maxInt, "0");
  }

  // Fraction clamp
  if (decimalPlaces > 0) {
    if (frac.length > decimalPlaces) frac = frac.slice(0, decimalPlaces);
  } else {
    frac = "";
  }

  let out = int;
  if (decimalPlaces > 0 && (hadDot || frac.length)) {
    if (trailingDot && frac.length === 0) out = int + ".";
    else if (frac.length > 0) out = int + "." + frac;
  }

  if (negative && out !== "") out = "-" + out;

  inputValue.value = out;
  emit("update:modelValue", out);
  emit("change", out);

  return out;
}

watch(inputValue, (newValue) => {
  if (typeof newValue === "string") {
    formatValue(newValue, {
      maxInt: props.maxNumberLength,
      pad: props.pad,
      decimalPlaces: props.decimalPlaces,
    });
  }
});

const emit = defineEmits({
  "update:modelValue": (_: any) => true,
  clear: () => true,
  blur: (e: any) => true,
  change: (text: string) => true,
  keydown: (e: KeyboardEvent) => true,
});

const attrs: Record<string, any> = useAttrs();
const slots = useSlots();
const inGroup = useInGroup();

const labelHorizontal = useHorizontalLabel(props);
const _labelWidth = useInputWidth(props);
const labelWidth = computed(() => _labelWidth.value || "auto");
const placeholder = usePlaceholder(props);
const inputEl = ref<HTMLInputElement | null>(null);

const id = computed(() => props.for || attrs.id || randomID("input"));
const instructionsId = computed(() => id.value + "-instructions");

const { proxy } = getCurrentInstance()!;

const {
  value,
  displayErrors: _displayErrors,
  valid,
  formTouched,
  touched,
} = useInputRules(
  props,
  () => {
    const inputs = proxy?.$el.getElementsByTagName("input");
    inputs[0]?.focus();
  },

  undefined,
  () => props.requiredText || `Enter a value for '${props.label || props.srLabel}'`,
);

const displayErrors = computed(
  () => attrs.displayErrors ?? attrs["display-errors"] ?? _displayErrors.value,
);

const originalValue = value.value;

const isInvalid = computed(() => {
  return (displayErrors.value && displayErrors.value.length) || props.invalid || !valid.value;
});

const classes = computed(() => {
  const invalid = isInvalid.value;
  return [
    labelHorizontal.value ? "flex-row items-center horizontal" : "flex-col",
    invalid && "with-errors",
    {
      required: validAttribute(attrs.required),
      readonly: validAttribute(attrs.readonly),
      disabled: validAttribute(attrs.disabled) ? true : undefined,
      valid: !invalid,
      error: invalid,
      empty: !value.value, //isEmpty
    },
  ];
});

const attributes = computed(() => {
  const attrs: Record<string, any> = {
    tabindex: 0,
    "data-autofocus": props.autofocus === true || void 0,
    rows: 0,
    "aria-label": props.label,
    disabled: props.disable || props.disabled,
  };

  return attrs;
});

const onEvents = computed(() => {
  const evt: Record<string, any> = {};

  return evt;
});

function onFocus(e: FocusEvent) {
  selectText();
}

function onBlur(e: FocusEvent) {
  emit("blur", e);
  // @ts-expect-error custom prop
  if (e && e.preventFormTouch) return;

  if (props.noValidation) return;

  if (formTouched) {
    setTimeout(() => {
      const t = (touched.value = touched.value || value.value !== originalValue);

      if (!proxy?.$.isUnmounted) {
        formTouched(t);
      }
    }, 50);
  }
}

function clearValue() {
  value.value = "";
  emit("clear");

  if (inputEl.value) {
    inputEl.value.focus();
  }
}

function selectText() {
  if (inputEl.value?.value) {
    inputEl.value?.select();
  }
}

const hasLabel = computed(
  () => props.label || attrs.required || slots.helper || props.helper || props.srLabel,
);

const focus = () => {
  inputEl.value?.focus();
};

defineExpose({
  focus,
  clearValue,
  selectText,
});

const steps = computed(() => {
  if (props.step) {
    return props.step;
  }

  return undefined;
});

// limit Number input to these keys
const NumberAllowedKeys = new Set([
  Key.Meta,
  Key.Backspace,
  Key.Shift,
  Key.Delete,
  Key.ArrowLeft,
  Key.ArrowRight,
  Key.ArrowUp,
  Key.ArrowDown,
  Key.Tab,
  "-",
  ".",
  "0",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
]);

function handleKeydown(e: KeyboardEvent) {
  if (validAttribute(attrs.disable) || validAttribute(attrs.disabled)) {
    e.preventDefault();
    return;
  }

  if (props.type === "number") {
    if (e.metaKey === false && e.ctrlKey === false) {
      if (NumberAllowedKeys.has(e.key) === false) {
        e.preventDefault();
        return;
      }

      if ((e.target as HTMLInputElement).value?.includes(".") && e.key === ".") {
        e.preventDefault();
        return;
      }
    }
  }

  emit("keydown", e);
}

// using inputmask to prevent
watch(
  [() => props.decimalPlaces, () => props.mask, () => props.inputFormat, inputEl, placeholder],
  ([digits, mask, inputFormat, input, placeholder], _, onCleanup) => {
    if (!input) return;
    if (!digits && !mask && !inputFormat) return;
  },
  {
    flush: "sync",
  },
);

// eslint-disable-next-line no-undef
if (IS_APP && (import.meta.env.DEV || location.host.startsWith("uat."))) {
  // TODO - why doesn't this work?
  checkLabel(props, "MInput", "label");
}
</script>
<template>
  <m-validation-component
    class="m-input medicus-outline"
    :errors="displayErrors"
    :class="$attrs.class"
    :actions="actions"
    :actions-left="actionsLeft"
  >
    <div
      :class="[
        'm-input--content',
        {
          horizontal: horizontal && !inGroup,
          'with-errors': isInvalid,
          'gap-1': hasLabel && !srLabel && !instructions,
          'in-group': inGroup,
          'inline-input': inline,
          disabled,
        },
      ]"
    >
      <label
        v-if="hasLabel"
        :id="`label-${id}`"
        :for="id"
        class="label gap-1"
        :class="{ 'sr-only': srLabel }"
      >
        <span>{{ label || srLabel }}</span>

        <input-helper v-if="$slots.helper || helper" :text="helper || ''">
          <slot name="helper" />
        </input-helper>
        <span v-if="validAttribute($attrs.required)" class="required">*</span>
      </label>
      <p v-if="props.instructions && !props.counter" :id="instructionsId" class="instructions">
        {{ props.instructions }}
      </p>
      <p v-else-if="props.srInstructions" :id="instructionsId" class="sr-only">
        {{ props.srInstructions }}
      </p>
      <div
        ref="container"
        class="m-input--input-box"
        :class="{
          disabled: disable || disabled,
          small,
          medium,
          large,
          border,
          'hide-focus-outline': hideFocusOutline,
        }"
        :role="role"
        @click="onClick"
      >
        <div v-if="$slots.prepend" class="m-input--input-box--prepend">
          <slot name="prepend" />
        </div>

        <slot
          name="input"
          v-bind="{
            id,
            autocomplete,
            min,
            max,
            maxlength,
            steps,
            inputRole,
            onBlur,
            handleKeydown,
          }"
        >
          <input
            v-bind="{ ...$attrs, ...attributes, ...onEvents, class: undefined }"
            :id="id"
            ref="inputEl"
            v-model="inputValue"
            :class="classes"
            :placeholder="placeholder"
            :autocomplete="autocomplete"
            :autofocus="autofocus"
            :min="min"
            :max="max"
            :step="steps"
            :role="inputRole"
            :aria-labelledby="`label-${id}`"
            :aria-describedby="instructionsId"
            @focus="onFocus"
            @blur="onBlur"
            @keydown="handleKeydown"
            @change="$emit('change', $event?.target?.value)"
          />
        </slot>

        <div
          v-if="$slots.append || clearable || showClear || suffix"
          class="m-input--input-box--append"
        >
          <m-button
            v-if="showClear === undefined ? clearable && value : showClear"
            ghost
            icon="fa-solid fa-circle-xmark"
            color="secondary"
            icon-only
            :label="`Clear ${srLabel || label}`"
            data-testid="input-clear-button"
            @click="clearValue"
          />
          <div v-if="loading" class="loading">
            <q-spinner />
          </div>
          <span v-if="suffix">{{ suffix }}</span>
          <slot name="append" />
        </div>
      </div>
    </div>
    <slot :el="$refs.container" />
  </m-validation-component>
</template>
<style lang="scss" scoped>
@import "./MInput.scss";
.m-input--content.inline-input {
  .label {
    padding-right: 0 !important;
  }
}
.m-input {
  .m-input--content {
    &.horizontal {
      grid-template-columns: v-bind(labelWidth) 1fr;
    }
  }
}
</style>
